import { ANIMATION_TIME } from '@config/screen'
import { Portal } from '@shared/layouts/portal/portal'
import cn from 'classnames'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'

import animationStyles from './animtaion.module.css'
import styles from './popup-inner.module.css'

type PopupInnerProps = {
  onClose: () => void
  children: ReactNode
  opened?: boolean
  withClose?: boolean
  withOverlay?: boolean
  animationType?: 'center' | 'right'
  contentCls?: string
}

const overlayCenterAnimation = {
  enter: animationStyles.overlayCenterEnter,
  enterActive: animationStyles.overlayCenterEnterActive,
  exit: animationStyles.overlayCenterExit,
  exitActive: animationStyles.overlayCenterExitActive
}
const overlayRightAnimation = {
  enter: animationStyles.overlayRightEnter,
  enterActive: animationStyles.overlayRightEnterActive,
  exit: animationStyles.overlayRightExit,
  exitActive: animationStyles.overlayRightExitActive
}

const contentCenterAnimation = {
  enter: animationStyles.contentCenterEnter,
  enterActive: animationStyles.contentCenterEnterActive,
  exit: animationStyles.contentCenterExit,
  exitActive: animationStyles.contentCenterExitActive
}

const contentRightAnimation = {
  enter: animationStyles.contentRightEnter,
  enterActive: animationStyles.contentRightEnterActive,
  exit: animationStyles.contentRightExit,
  exitActive: animationStyles.contentRightExitActive
}

export const PopupInner = ({
  onClose,
  children,
  withClose = true,
  opened = false,
  withOverlay = true,
  animationType = 'center',
  contentCls
}: PopupInnerProps) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const [animationIn, setAnimationIn] = useState(false)

  useEffect(() => {
    setAnimationIn(opened)
    return () => setAnimationIn(false)
  }, [opened])

  const handleEscClick = useCallback(
    (e: KeyboardEvent) => {
      if ((e.charCode || e.keyCode) === 27) {
        onClose()
      }
    },
    [onClose]
  )
  useEffect(() => {
    document.body.addEventListener('keydown', handleEscClick)
    return function cleanup() {
      document.body.removeEventListener('keydown', handleEscClick)
    }
  }, [handleEscClick])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  return (
    <Portal>
      <div className={styles.popup} role="dialog">
        <CSSTransition
          in={animationIn}
          nodeRef={overlayRef}
          timeout={ANIMATION_TIME}
          mountOnEnter
          unmountOnExit
          classNames={
            animationType === 'center'
              ? overlayCenterAnimation
              : overlayRightAnimation
          }
        >
          <div
            ref={overlayRef}
            className={cn(styles.overlay, {
              [styles.light]: withOverlay === false
            })}
            role="button"
            tabIndex={0}
            onClick={onClose}
          />
        </CSSTransition>
        {withClose && (
          <div
            className={styles.close}
            role="button"
            tabIndex={1}
            onClick={onClose}
          />
        )}
        <CSSTransition
          in={animationIn}
          nodeRef={contentRef}
          timeout={ANIMATION_TIME}
          mountOnEnter
          unmountOnExit
          classNames={
            animationType === 'center'
              ? contentCenterAnimation
              : contentRightAnimation
          }
        >
          <div ref={contentRef} className={cn(styles.content, contentCls)}>
            {children}
          </div>
        </CSSTransition>
      </div>
    </Portal>
  )
}
